import { gql, useMutation, useQuery } from '@apollo/client'
import { Dispatch, useState } from 'react'

import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { IAction, IErrorAction } from 'checkout/containers/CheckoutStateReducer'
import { PartialOrder } from 'checkout/types/IOrder'
import { ORDER_PROPS, SHIPPING_ADDRESS_COUNT } from './useOrder'

interface IUseBraintree {
  dispatch: Dispatch<IAction | IErrorAction>
  order?: PartialOrder
}

// we can't use the type imported from the lib bc of the server render issue below
export interface IBraintreePaypalInstance {
  teardown: () => void
  paypal: {
    initAuthFlow: () => void
    closeAuthFlow: () => void
  }
}

const GET_BRAIN_TREE_TOKEN = gql`
  query getBrainTreeToken {
    brainTreeToken
  }
`

const ORDER_WITH_PAYPAL = gql`
  mutation buyPayPal($input: BuyPayPalInput!, $shippingAddressCount: Int) {
    buyPayPal(input: $input) {
      ...OrderProps
    }
  }
  ${ORDER_PROPS}
`

export const useBraintree = ({ dispatch, order }: IUseBraintree) => {
  const [braintreeClient, setBraintreeClient] = useState<
    IBraintreePaypalInstance | null | undefined
  >(null)
  const [braintreeLibrary, setBraintreeLibrary] = useState(null)
  const [isBraintreeLoading, setIsBraintreeLoading] = useState(false)
  const { refetch: getBraintreeTokenQuery } = useQuery(GET_BRAIN_TREE_TOKEN, { skip: true })
  const [orderWithPayPal] = useMutation(ORDER_WITH_PAYPAL)

  const initBraintree = async () => {
    if (braintreeClient || braintreeLibrary || isBraintreeLoading) {
      return
    }

    // this version of the SDK uses window.navigator onload which isn't available on server render
    // so we need to defer loading of this SDK to run client-side only
    setIsBraintreeLoading(true)
    const Braintree = await import('braintree-web')
    setBraintreeLibrary(Braintree)

    const paypalShippingAddress = order?.shippingAddress

    const getBraintreeToken = async () => {
      try {
        const { data } = await getBraintreeTokenQuery()
        return data.brainTreeToken
      } catch (error) {
        Bugsnag.notify(error as NotifiableError)
      }
    }

    const brainTreeToken = await getBraintreeToken()

    Braintree.setup(brainTreeToken, 'custom', {
      paypal: {
        displayName: 'Flight Club',
        headless: true, // tells the API we don't want the default PayPal button
        enableShippingAddress: true,
        shippingAddressOverride: {
          recipientName: paypalShippingAddress?.name,
          streetAddress: `${paypalShippingAddress?.address1} ${paypalShippingAddress?.address2}`,
          city: paypalShippingAddress?.city,
          countryCode: paypalShippingAddress?.countryCode,
          postalCode: paypalShippingAddress?.postalCode,
          state: paypalShippingAddress?.state,
          phone: paypalShippingAddress?.phone,
          locality: paypalShippingAddress?.city,
          countryCodeAlpha2: paypalShippingAddress?.countryCode,
          region: paypalShippingAddress?.state,
        },
      },
      onReady: (integration) => {
        setBraintreeClient(integration)
        setIsBraintreeLoading(false)
      },
      onPaymentMethodReceived: async ({
        nonce,
        details,
      }: {
        nonce: string
        details: { email: string }
      }) => {
        // set loading status before server calls
        try {
          dispatch({ type: 'paymentMethodRequest' })
          const input = {
            orderNumber: order?.orderNumber,
            braintreePaymentMethodNonce: nonce,
            finalPriceCents: order?.finalPrice?.value,
            email: details?.email,
          }
          const { data } = await orderWithPayPal({
            variables: { input, shippingAddressCount: SHIPPING_ADDRESS_COUNT },
          })

          dispatch({
            type: 'paymentMethodRequestSuccess',
            payload: data.buyPayPal,
          })
        } catch (error) {
          Bugsnag.notify(error as NotifiableError)
          dispatch({
            type: 'paymentMethodRequestFailure',
            payload: error.message,
          })
        }
      },
    })
  }

  const selectPayPal = () => {
    braintreeClient?.paypal?.initAuthFlow()
  }

  return {
    initBraintree,
    selectPayPal,
  }
}
